import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Download, FileText, User, Phone, MapPin, DollarSign, Building, Shield } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import ProductionLine from './ProductionLine';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function ClientDetails() {
  const { darkMode } = useSettings();
  const navigate = useNavigate();
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [ratingData, setRatingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProductionLine, setShowProductionLine] = useState(false);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const [clientRes, ratingRes] = await Promise.all([
          axios.get(`${API_URL}/leads/${id}`),
          axios.get(`${API_URL}/rating-data/${id}`)
        ]);
        setClient(clientRes.data);
        setRatingData(ratingRes.data);
      } catch (error) {
        console.error('Erro ao buscar dados do cliente:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchClientData();
  }, [id]);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  if (!client || !ratingData) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Cliente não encontrado</div>
      </div>
    );
  }

  const isPF = ratingData.tipo_pessoa === 'PF';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/clients')}
            className={`p-2 rounded-lg ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            <ArrowLeft size={20} className={darkMode ? 'text-white' : 'text-gray-700'} />
          </button>
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {client.nome}
            </h1>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
              {client.cpf_cnpj} • {isPF ? 'Pessoa Física' : 'Pessoa Jurídica'}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowProductionLine(true)}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <FileText size={20} />
            Esteira de Produção
          </button>
        </div>
      </div>

      {/* Dados Pessoais */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <User size={24} />
          Dados Pessoais
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nome Completo</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.nome}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {isPF ? 'CPF' : 'CNPJ'}
            </label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.cpf_cnpj}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>RG</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.rg}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Data de Expedição do RG</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.rg_expedicao || 'N/A'}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Data de Nascimento</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.data_nascimento}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sexo</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.sexo}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nome do Pai</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.nome_pai}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nome da Mãe</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.nome_mae}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Estado Civil</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.estado_civil}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Número de Dependentes</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.numero_dependentes || 0}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Título de Eleitor</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.titulo_eleitor}</p>
          </div>
        </div>

        {/* Dados do Cônjuge (se aplicável) */}
        {isPF && ratingData.estado_civil !== 'Solteiro(a)' && ratingData.nome_conjuge && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dados do Cônjuge</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nome do Cônjuge</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.nome_conjuge}</p>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>CPF do Cônjuge</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.cpf_conjuge || 'N/A'}</p>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Data de Nascimento do Cônjuge</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.data_nascimento_conjuge || 'N/A'}</p>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Certidão de Casamento</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {ratingData.certidao_casamento ? 'Enviada' : 'Pendente'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Certidão de Nascimento (se solteiro) */}
        {isPF && ratingData.estado_civil === 'Solteiro(a)' && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Certidão de Nascimento</h3>
            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Certidão de Nascimento</label>
              <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {ratingData.certidao_nascimento ? 'Enviada' : 'Não enviada (opcional)'}
              </p>
              <p className={`text-xs mt-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                ⚠️ Nota: Se o cliente não possuir este documento, o serviço pode ser finalizado normalmente.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contato */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Phone size={24} />
          Contato
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>E-mail</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.email}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Telefone Principal</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.telefone_principal}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Outros Telefones</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.outros_telefones || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <MapPin size={24} />
          Endereço Residencial
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Endereço</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.endereco}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>CEP</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.cep}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Bairro/Cidade</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.cidade_imovel || 'N/A'}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Município</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.cidade_imovel || 'N/A'}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Estado</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.estado_imovel || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Dados Financeiros */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <DollarSign size={24} />
          Dados Financeiros
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nível de Escolaridade</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.nivel_escolaridade}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Profissão</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.profissao}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Média Salarial</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              R$ {ratingData.media_salarial?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Dia do Pagamento</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.dia_pagamento}</p>
          </div>
        </div>
      </div>

      {/* Dados SERASA */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Shield size={24} />
          Dados SERASA
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Título de Eleitor</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.titulo_eleitor}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Senha SERASA</label>
            <p className={`text-lg font-mono ${darkMode ? 'text-white' : 'text-gray-900'}`}>••••••••</p>
          </div>
        </div>
      </div>

      {/* Patrimônio - Imóvel */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Building size={24} />
          Patrimônio - Imóvel
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Situação</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.tipo_imovel}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Complemento</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.complemento_imovel}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tipo do Imóvel</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.tipo_imovel_detalhe}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cidade</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.cidade_imovel}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Estado</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.estado_imovel}</p>
          </div>
          <div>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Valor do Imóvel</label>
            <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              R$ {ratingData.valor_imovel?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>

        {/* Veículo (se aplicável) */}
        {ratingData.possui_veiculo && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Patrimônio - Veículo</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Valor</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  R$ {ratingData.veiculo_valor?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Ano de Fabricação</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.veiculo_ano}</p>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Placa</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.veiculo_placa}</p>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Estado da Placa</label>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ratingData.veiculo_placa_estado}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Esteira de Produção */}
      {showProductionLine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <ProductionLine
              lead={client}
              onClose={() => setShowProductionLine(false)}
              onUpdate={(updatedLead) => {
                setClient(updatedLead);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDetails;
